// @ts-check
// Subtítulo dos resultados da busca do menu de comandos (doc 15, seção 9.2, versão enxuta do card de prévia):
// telefone ou e-mail da pessoa, etapa do card, cidade da família, status da tarefa. Os registros vêm da API já
// cortados pelo papel, então campo oculto (por exemplo telefone restrito) simplesmente não aparece.

/**
 * @typedef {{ name: string, type: string, options?: { value: string, label: string }[] | null }} PreviewField
 * @typedef {{ nameSingular: string, fields: PreviewField[] }} PreviewObject
 * @typedef {{ translateLabel: (label: string) => string, formatPhone: (phones: any) => string, formatDate: (value: string) => string }} PreviewFormatters
 */

// campos preferidos por objeto, na ordem; o primeiro com valor vira o subtítulo
export const SUBTITLE_FIELDS_BY_OBJECT = Object.freeze(/** @type {Record<string, string[]>} */ ({
  person: ['phones', 'emails'],
  enrollmentOpportunity: ['stage'],
  household: ['householdAddress'],
  task: ['status', 'dueAt'],
  company: ['domainName', 'phones'],
  opportunityStudent: ['effectiveStage', 'stageOverride'],
  enrollment: ['status'],
  application: ['status'],
}));
const FALLBACK_FIELD_NAMES = ['stage', 'status'];
const FALLBACK_FIELD_TYPES = ['PHONES', 'EMAILS'];

/**
 * Texto de um valor no subtítulo; vazio quando o campo não tem valor.
 * @param {PreviewField} field @param {any} value @param {PreviewFormatters} formatters
 */
export function formatPreviewValue(field, value, formatters) {
  if (value === null || value === undefined || value === '') return '';
  switch (field.type) {
    case 'SELECT': {
      const option = field.options?.find((candidate) => candidate.value === value);
      return option ? formatters.translateLabel(option.label) : String(value);
    }
    case 'PHONES':
      return value.primaryPhoneNumber ? formatters.formatPhone(value) : '';
    case 'EMAILS':
      return value.primaryEmail ?? '';
    case 'ADDRESS':
      return [value.addressCity, value.addressState].filter(Boolean).join(' / ');
    case 'LINKS':
      return value.primaryLinkUrl ?? '';
    case 'DATE':
    case 'DATE_TIME':
      return formatters.formatDate(value);
    default:
      return typeof value === 'object' ? '' : String(value);
  }
}

/**
 * @param {PreviewObject} objectMetadata @param {Record<string, any>} record @param {PreviewFormatters} formatters
 * @returns {string}
 */
export function buildRecordSubtitle(objectMetadata, record, formatters) {
  const fieldsByName = new Map(objectMetadata.fields.map((field) => [field.name, field]));
  const preferred = (SUBTITLE_FIELDS_BY_OBJECT[objectMetadata.nameSingular] ?? []).map((name) => fieldsByName.get(name));
  const fallback = [
    ...FALLBACK_FIELD_NAMES.map((name) => fieldsByName.get(name)),
    ...objectMetadata.fields.filter((field) => FALLBACK_FIELD_TYPES.includes(field.type)),
  ];
  for (const field of [...preferred, ...fallback]) {
    if (!field || !(field.name in record)) continue;
    const text = formatPreviewValue(field, record[field.name], formatters);
    if (text) return text;
  }
  return '';
}

/**
 * Ids por objeto, para buscar os registros dos resultados num pedido por objeto.
 * @param {{ objectNameSingular: string, recordId: string }[]} results
 * @returns {Map<string, string[]>}
 */
export function groupResultIdsByObject(results) {
  /** @type {Map<string, string[]>} */
  const grouped = new Map();
  for (const result of results) {
    const ids = grouped.get(result.objectNameSingular) ?? [];
    if (!ids.includes(result.recordId)) ids.push(result.recordId);
    grouped.set(result.objectNameSingular, ids);
  }
  return grouped;
}

/**
 * Chips de filtro por objeto: os de maior prioridade primeiro (Pessoas, Famílias, Oportunidades), depois os demais
 * pesquisáveis na ordem do menu.
 * @template {{ nameSingular: string, isSearchable?: boolean }} TObject
 * @param {TObject[]} objects
 * @returns {TObject[]}
 */
export function orderSearchFilterObjects(objects) {
  const PRIORITY = ['person', 'household', 'enrollmentOpportunity', 'task', 'enrollment', 'company'];
  const searchable = objects.filter((object) => object.isSearchable !== false);
  const rank = (/** @type {TObject} */ object) => {
    const index = PRIORITY.indexOf(object.nameSingular);
    return index < 0 ? PRIORITY.length : index;
  };
  return searchable
    .map((object, position) => ({ object, position }))
    .sort((first, second) => rank(first.object) - rank(second.object) || first.position - second.position)
    .map(({ object }) => object);
}
